/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import React, { useState } from 'react';
import {
  type Customer,
  type LoanDetails,
  type Loan,
} from '../store/customerTypes';
import { Table, Modal, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch, type RootState } from '../../../redux/store';
import { getPledgeByIdAction } from '../../Pledge/store/pledgeActions';
import { resetPledgeState } from '../../Pledge/store/pledgeReducer';
import PledgeDetailsIndex from '../../Pledge/PledgeDetails';
import PlusFilledWhite from '../../../assets/icons/plus-filled-white.svg';
import LoanModal from '../LoanModal';
import PaymentModal from '../PaymentModal';
import ActionModal from '../ActionModal';
import PrintableContent from '../PrintableContent';
import LoanPrintableContent from '../../Loan/PrintableContent';
import { type Loan as PrintLoan } from '../../Loan/PrintableContent/printTypes';
// import { getLoanByLoanNumberAction } from '../../Loan/store/loanActions';

interface Props {
  loan?: LoanDetails;
  customer?: Customer;
  refetchCustomer: (customerId: string) => void;
}

export default function loanDetailsIndex({
  loan,
  customer,
  refetchCustomer,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  // Cashier, superadmin and manager: pledge numbers open the Pledge page.
  // Billing: pledge numbers open a view-only popup (no route).
  const role = window.localStorage.getItem('role');
  const canOpenPledge =
    role === 'cashier' ||
    role === 'superadmin' ||
    role === 'manager' ||
    role === 'billing';

  const { pledgeResponse }: any = useSelector<RootState>(
    (rootState) => rootState.pledge
  );
  const [isPledgeModalOpen, setIsPledgeModalOpen] = useState(false);
  const [isPledgeLoading, setIsPledgeLoading] = useState(false);

  const openPledge = async (pledgeNumber: string) => {
    if (role === 'cashier' || role === 'superadmin' || role === 'manager') {
      navigate(`/pledge?pledgeID=${pledgeNumber}`);
      return;
    }
    // full page loader first, popup opens once the pledge has loaded
    setIsPledgeLoading(true);
    try {
      const failed = await getPledgeByIdAction(dispatch, { pledgeNumber });
      if (failed !== false) {
        setIsPledgeModalOpen(true);
      }
    } finally {
      setIsPledgeLoading(false);
    }
  };

  const closePledgeModal = () => {
    setIsPledgeModalOpen(false);
    dispatch(resetPledgeState());
  };

  const [selectedLoan, setSelectedLoan] = useState<Loan>();
  const [selectedLoanTransactions, setSelectedLoanTransactions] =
    useState<any>();

  const [printSelectedLoan, setPrintSelectedLoan] = useState<PrintLoan>();

  const nonActiveLoanColumns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: Loan, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Loan No.',
      dataIndex: 'loanNumber',
      width: 46,
      render: (_: any, record: Loan) => (
        <p className="closed__loan__data">{record.loanNumber}</p>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'actions',
      width: 120,
      render: (_: any, record: Loan) => (
        <p
          style={{
            color: '#DD1E2D',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          onClick={async () => {
            setSelectedLoan(record);

            setSelectedLoanTransactions(record.transactions);
            showModal();
          }}
        >
          View Details
        </p>
      ),
    },
  ];

  const closedLoanColumns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: Loan, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Loan No.',
      dataIndex: 'loanNumber',
      width: 46,
      render: (_: any, record: Loan) => (
        <p className="closed__loan__data">{record.loanNumber}</p>
      ),
    },
    {
      title: 'Pledge No.',
      dataIndex: 'pledgeNumber',
      width: 46,
      render: (_: any, record: Loan) => {
        const pledgeNumbers = new Set(
          record?.jewels
            .filter((jewel: any) => jewel.isPledged)
            .map((jewel: any) => jewel.pledgeNumber)
        );
        return (
          <p className="closed__loan__data">
            {Array.from(pledgeNumbers).map((x) => (
              <p
                className="data"
                style={
                  canOpenPledge
                    ? {
                        margin: 0,
                        color: '#DD1E2D',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }
                    : { margin: 0 }
                }
                key={x}
                onClick={
                  canOpenPledge
                    ? () => {
                        void openPledge(String(x));
                      }
                    : undefined
                }
              >
                {x}
              </p>
            ))}
          </p>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'actions',
      width: 120,
      render: (_: any, record: Loan) => (
        <p
          style={{
            color: '#DD1E2D',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          onClick={async () => {
            setSelectedLoan(record);

            setSelectedLoanTransactions(record.transactions);
            showModal();
          }}
        >
          View Details
        </p>
      ),
    },
    {
      title: 'Print',
      dataIndex: 'print',
      width: 120,
      render: (_: any, record: Loan) => (
        <p
          style={{
            width: 73,
            height: 34,
            borderRadius: 12,
            backgroundColor: '#DD1E2D',
            color: 'white',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => {
            const printLoanDetail = JSON.parse(
              JSON.stringify(record)
            ) as PrintLoan;
            setPrintSelectedLoan(printLoanDetail);
            setIsLoanPrintModalOpen(true);
          }}
        >
          Print
        </p>
      ),
    },
  ];

  const activeLoanColumns = [
    ...closedLoanColumns,
    {
      title: 'Pay',
      dataIndex: 'pay',
      width: 120,
      render: (_: any, record: Loan) => {
        const hasActionOrSales = record.jewels.some(
          (item: any) =>
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            item.closureType &&
            (item.closureType === 'Action' || item.closureType === 'Sales')
        );
        return hasActionOrSales ? (
          <p
            style={{
              width: 73,
              height: 34,
              borderRadius: 12,
              backgroundColor: '#DD1E2D',
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              setSelectedLoan(record);
              showActionModal();
            }}
          >
            Action
          </p>
        ) : (
          <p
            style={{
              width: 73,
              height: 34,
              borderRadius: 12,
              backgroundColor: '#DD1E2D',
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              setSelectedLoan(record);
              showPayModal();
            }}
          >
            Pay
          </p>
        );
      },
    },
  ];

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isCopyPrintModalOpen, setIsCopyPrintModalOpen] = useState(false);
  const [isLoanPrintModalOpen, setIsLoanPrintModalOpen] = useState(false);

  const [receiptNumber, setReceiptNumber] = useState('');
  const [paymentType, setPaymentType] = useState('');

  const [receiptPrincipal, setReceiptPrincipal] = useState(0);
  const [receiptInterest, setReceiptInterest] = useState(0);

  const handlePrintClose = () => {
    setIsPrintModalOpen(false);
    if (isPrintModalOpen) {
      refetchCustomer(String(customer?.customerId));
    } else {
      setIsCopyPrintModalOpen(false);
    }
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const showPayModal = () => {
    setIsPayModalOpen(true);
  };

  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const showActionModal = () => {
    setIsActionModalOpen(true);
  };

  const [interestDaysCount, setInterestDaysCount] = useState<number>(0);
  const [pendingPrincipal, setPendingPrincipal] = useState<number>(0);
  const [currentInterestRate, setCurrentInterestRate] = useState<number>(0);
  const [fullTransactionDate, setFullTransactionDate] = useState('');

  const handlePayClose = (
    receiptNumber: string,
    principal: number,
    interest: number,
    paymentType: string,
    interestDays: number,
    transactionDate: string,
    remainingPrincipal: number,
    currentInterestRate: number
  ) => {
    setIsPayModalOpen(false);
    if (receiptNumber !== '') {
      setReceiptNumber(receiptNumber);
      setReceiptPrincipal(principal);
      setReceiptInterest(interest);

      setPaymentType(paymentType);
      setInterestDaysCount(interestDays);
      setFullTransactionDate(transactionDate);
      setIsPrintModalOpen(true);
      setPendingPrincipal(remainingPrincipal);
      setCurrentInterestRate(currentInterestRate);
    } else {
      setIsPayModalOpen(false);
    }
  };

  const duplicateCopy = (
    receiptNumber: string,
    principal: number,
    interest: number,
    paymentType: string,
    interestDays: number,
    transactionDate: string,
    pendingPrincipal: number,
    currentInterestRate: number
  ) => {
    if (receiptNumber !== '') {
      setReceiptNumber(receiptNumber);
      setReceiptPrincipal(principal);
      setReceiptInterest(interest);
      setPaymentType(paymentType);
      setInterestDaysCount(interestDays);
      setFullTransactionDate(transactionDate);
      setIsCopyPrintModalOpen(true);
      setPendingPrincipal(pendingPrincipal);
      setCurrentInterestRate(currentInterestRate);
    } else {
      setIsCopyPrintModalOpen(false);
    }
  };

  const handlePayCloseNOOP = () => {
    setIsPayModalOpen(false);
  };

  return (
    <div className="loan__details__page">
      {/* <p className="pending__loan__title">Pending Interest: 1000</p> */}

      <div className="closed__loan__section">
        <div className="current__loan__header">
          <p className="closed__loan__title">Current Loans</p>
          <a
            className="create__loan__button"
            href={`/loan?customerId=${customer?.customerId}`}
          >
            <img className="plus__icon" src={PlusFilledWhite} /> Create Loan
          </a>
        </div>

        <Table
          dataSource={loan?.activeLoan}
          columns={activeLoanColumns}
          pagination={false}
          rowKey={(record: Loan) => record.loanNumber.toString()}
        />
      </div>
      <div className="closed__loan__section">
        <p className="closed__loan__title">Closed Loans</p>
        <Table
          dataSource={loan?.closedLoan}
          columns={closedLoanColumns}
          pagination={false}
          rowKey={(record: Loan) => record.loanNumber.toString()}
        />
      </div>
      <div className="closed__loan__section">
        <div className="current__loan__header">
          <p className="closed__loan__title">Cash disbursement pending</p>
        </div>

        <Table
          dataSource={loan?.cashPending}
          columns={closedLoanColumns}
          pagination={false}
          rowKey={(record: Loan) => record.loanNumber.toString()}
        />
      </div>
      <div className="closed__loan__section">
        <div className="current__loan__header">
          <p className="closed__loan__title">Loan Approvals Pending</p>
        </div>

        <Table
          dataSource={loan?.approvals}
          columns={nonActiveLoanColumns}
          pagination={false}
          rowKey={(record: Loan) => record.loanNumber.toString()}
        />
      </div>
      <div className="closed__loan__section">
        <div className="current__loan__header">
          <p className="closed__loan__title">Loans Rejected</p>
        </div>

        <Table
          dataSource={loan?.rejected}
          columns={nonActiveLoanColumns}
          pagination={false}
          rowKey={(record: Loan) => record.loanNumber.toString()}
        />
      </div>
      <Modal
        width={1129}
        title="Loan Details"
        open={isModalOpen}
        onCancel={handleClose}
        footer={() => <></>}
      >
        <LoanModal
          openPrintModal={(transaction: any) => {
            duplicateCopy(
              // payments made before bill numbers existed keep their old receipt number
              transaction.billNumber ??
                `${selectedLoan?.loanNumber} - ${transaction._id.slice(0, 6)}`,
              Number(transaction.principal),
              Number(transaction.interestAmount),
              transaction.paymentType,
              transaction.interestDays,
              transaction.transactionDate,
              selectedLoan?.remainingPrincipal ?? 0,
              transaction.currentInterestRate
            );
          }}
          transactions={selectedLoanTransactions}
          customer={customer}
          loanDetail={selectedLoan}
        />
      </Modal>
      <Modal
        width={727}
        title={`Pay Loan - ${customer?.name} (Loan No : ${selectedLoan?.loanNumber})`}
        open={isPayModalOpen}
        onCancel={() => {
          handlePayClose('', 0, 0, '', 0, '', 0, 0);
        }}
        destroyOnClose
        footer={() => <></>}
      >
        <PaymentModal
          closeModal={handlePayClose}
          closeModalNOOP={handlePayCloseNOOP}
          customer={customer}
          loanDetail={selectedLoan}
        />
      </Modal>
      <Modal
        width={727}
        title={`Action Loan - ${customer?.name} (Loan No : ${selectedLoan?.loanNumber})`}
        open={isActionModalOpen}
        onCancel={() => {
          setIsActionModalOpen(false);
          setSelectedLoan(undefined);
        }}
        destroyOnClose
        footer={() => <></>}
      >
        <ActionModal
          closeModal={() => {
            setIsActionModalOpen(false);
            setSelectedLoan(undefined);
          }}
          closeModalNOOP={() => {}}
          customer={customer}
          loanDetail={selectedLoan}
        />
      </Modal>
      <Modal
        width={1020}
        title={
          <div style={{ display: 'flex', flexDirection: 'row', gap: 24 }}>
            <span>Payment receipt</span>
          </div>
        }
        open={isPrintModalOpen || isCopyPrintModalOpen}
        onCancel={() => {
          handlePrintClose();
        }}
        zIndex={2000}
        footer={() => <></>}
      >
        {selectedLoan !== undefined && customer !== undefined && (
          <PrintableContent
            receiptNumber={receiptNumber}
            receiptPrincipal={receiptPrincipal}
            receiptInterest={receiptInterest}
            paymentType={paymentType}
            loanDetails={selectedLoan}
            customer={customer}
            interestDays={interestDaysCount}
            tillDate={fullTransactionDate}
            pendingPrincipal={pendingPrincipal}
            currentInterestRate={currentInterestRate}
          />
        )}
      </Modal>
      <Modal
        width={1020}
        title={
          <div style={{ display: 'flex', flexDirection: 'row', gap: 24 }}>
            <span>Loan summary</span>
          </div>
        }
        open={isLoanPrintModalOpen}
        onCancel={() => {
          setIsLoanPrintModalOpen(false);
        }}
        footer={() => <></>}
      >
        {printSelectedLoan !== undefined && customer !== undefined && (
          <LoanPrintableContent
            loanDetails={printSelectedLoan}
            customer={customer}
            type="loan"
          />
        )}
      </Modal>
      <Modal
        width={1100}
        title={null}
        open={isPledgeModalOpen}
        onCancel={closePledgeModal}
        footer={null}
        destroyOnClose
      >
        {pledgeResponse?.pledgeNumber !== undefined &&
          pledgeResponse.pledgeNumber !== '' && (
            <PledgeDetailsIndex pledge={pledgeResponse} viewOnly />
          )}
      </Modal>
      <Spin spinning={isPledgeLoading} fullscreen />
    </div>
  );
}
